"use client";

import { useEffect, useState, type RefObject } from "react";
import { gridMetrics } from "@/components/hero/HeroGrid";

/**
 * Corner-ribbon anchor for a section head (extracted from McpSection so the
 * roadmap sashes share one geometry). The owner wants the ribbon at the
 * TITLE BOX's corner — in grid mode that's the head block's GRID CLEARING
 * (the cell-snapped cleared rect GridClearing paints behind it), not the raw
 * block edge — "the corner of the title box, before the grid starts again".
 * Same snap math as GridClearing.tsx (shared EPS with RowBand/useBandRowAlign;
 * see that file for why). Off-grid (or below md, where there is no clearing)
 * it anchors to the head block's own rendered box instead.
 *
 * `wrapRef` is a `position: relative` wrapper holding the ribbon and the
 * SectionHead (the first `[data-grid-block]` inside it). Returns the inline
 * `{ top, right }` override for `CornerRibbon`. Re-measured on
 * resize/fonts.ready/grid-relayout with a change-guard, same idiom as the
 * kit's own layout hooks.
 */
export default function useRibbonAnchor(
  wrapRef: RefObject<HTMLElement | null>,
  onGrid: boolean,
): { top: number; right: number } {
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    const layout = () => {
      const wrap = wrapRef.current;
      const head = wrap?.querySelector<HTMLElement>("[data-grid-block]");
      if (!wrap || !head) return;
      const w = document.documentElement.clientWidth;
      const wrapRect = wrap.getBoundingClientRect();
      const headRect = head.getBoundingClientRect();
      let top: number;
      let right: number;
      if (onGrid && w >= 768) {
        const { cell, offsetX } = gridMetrics(w);
        const scrollY = window.scrollY;
        const EPS = 1.5;
        const clearRight = offsetX + Math.ceil((headRect.right - offsetX - EPS) / cell) * cell;
        const clearTop = Math.floor((headRect.top + scrollY + EPS) / cell) * cell;
        top = clearTop - (wrapRect.top + scrollY);
        right = wrapRect.right - clearRight;
      } else {
        top = headRect.top - wrapRect.top;
        right = wrapRect.right - headRect.right;
      }
      setPos((prev) => (prev.top === top && prev.right === right ? prev : { top, right }));
    };
    layout();
    document.fonts.ready.then(layout).catch(() => {});
    window.addEventListener("resize", layout);
    window.addEventListener("grid-relayout", layout);
    return () => {
      window.removeEventListener("resize", layout);
      window.removeEventListener("grid-relayout", layout);
    };
  }, [wrapRef, onGrid]);

  return pos;
}
